import { useState, useEffect } from 'react';
import { bisector } from 'd3-array';
import { localPoint } from '@visx/event';
import { LinePath } from '@visx/shape';
import type { ScaleTime, ScaleLinear } from 'd3-scale';
import { getTimeSeriesPeriodMap } from '@/utilities/timeSeriesPeriodMap';
import type { Period } from '@/utilities/timeSeriesPeriodMap';
import { useTheme } from '@simplywallst/ui-core';

import { Wrapper, Now } from './styled';
import { curveStepAfter } from 'd3-shape';
import { useNarrativeValuationHistory } from './hooks/useNarrativeValuationHistory';
import { cn } from '@simplywallst/ui-core/next';

export type HoverParam = {
  time: number;
  value: number | null;
  fairValue: number | null;
};

interface Props {
  height: number;
  keys: number[];
  entities: Record<number, { originalValue: number | null; value: number }>;
  periods: ReturnType<typeof getTimeSeriesPeriodMap>;
  period: Period;
  positionX: number;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  onClick?: () => void;
  valuations?:
    | {
        originalFairValue: number | null;
        fairValue: number | null;
        createdAt: string;
      }[]
    | null;
  onHover?: (param: HoverParam) => void;
  priceFillColor?: string;
}

const bisectDate = bisector((d) => d).left;

export const NarrativeMarker = ({
  height,
  positionX,
  keys,
  entities,
  periods,
  period,
  xScale,
  yScale,
  onClick,
  valuations,
  onHover,
  priceFillColor,
}: Props) => {
  const theme = useTheme();
  const [index, setIndex] = useState<number>(bisectDate(keys, periods.NOW));
  const nextPeriods = getTimeSeriesPeriodMap(
    new Date(keys[index] ? keys[index] : periods.NOW)
  );
  const [isInteracting, setIsInteracting] = useState(false);

  const x = xScale(nextPeriods.NOW);

  const { valuationTimestamps, valuationsByDate } =
    useNarrativeValuationHistory({
      keys,
      valuations,
      xScale,
      period,
    });

  useEffect(() => {
    if (keys.length > 0 && index > keys.length - 1) {
      const lastIndex = keys.length - 1;
      setIndex(lastIndex);
    }
  }, [keys, index]);

  const handleMove = (e) => {
    const point = localPoint(e);

    let x = 0;
    if (point !== null && point.x >= 0) {
      x = point.x;
    } else if (point !== null && point.x < 0) {
      // Fallback if Firefox has a negative point.x
      const rect = e.target.getBoundingClientRect();
      x = e.clientX - rect.x;
    }

    const date = xScale.invert(x);
    const nextIndex = bisectDate(keys, date);

    if (nextIndex !== index && nextIndex <= keys.length - 1) {
      setIndex(nextIndex);
      if (onHover) {
        const selectedTimestamp = keys[nextIndex];
        const closestFairValue =
          valuationsByDate[selectedTimestamp]?.originalValue;
        onHover({
          time: keys[nextIndex],
          value: entities[keys[nextIndex]]?.originalValue,
          fairValue: closestFairValue ?? null,
        });
      }
    }
  };

  const selectedTimestamp = keys[index];
  const fairValue = valuationsByDate[selectedTimestamp];

  const markerClasses = cn(
    'transition-opacity duration-200',
    isInteracting ? 'opacity-100' : 'opacity-0'
  );
  return (
    <Wrapper>
      <svg
        width="100%"
        height={height}
        transform={`translate(${positionX}, 0)`}
        className="touch-none"
        onMouseEnter={() => setIsInteracting(true)}
        onMouseLeave={() => setIsInteracting(false)}
        onTouchStart={() => setIsInteracting(true)}
        onTouchEnd={() => setIsInteracting(false)}
        aria-label="Narrative marker"
        role="img"
      >
        {keys.length > 0 && (
          <>
            <Now
              y="0"
              x={x}
              width="1"
              height={height}
              fill={theme.color.brand01}
              hover
            />
            {!!valuations?.length && (
              <LinePath
                curve={curveStepAfter}
                data={valuationTimestamps}
                x={(d) => xScale(d || 0) as number}
                y={(d) => yScale(valuationsByDate[d]?.value || 0) as number}
                className="stroke-chart-03"
                strokeWidth={1}
                strokeLinecap="round"
                fill="none"
                aria-label="Narrative valuation line"
                role="img"
              />
            )}

            <>
              <circle
                transform={`translate(${x + 0.5} ${yScale(
                  entities[keys[index]]?.value || 0
                )})`}
                r="3"
                fill={priceFillColor}
                stroke={theme.color.white}
                strokeWidth="1"
                className={markerClasses}
                aria-label="Narrative price marker"
                role="img"
              />
              {fairValue && (
                <circle
                  transform={`translate(${x + 0.5} ${yScale(
                    fairValue.value || 0
                  )})`}
                  r="3"
                  fill={theme.color.chart03}
                  stroke={theme.color.white}
                  strokeWidth="1"
                  className={markerClasses}
                  aria-label="Narrative fair value marker"
                  role="img"
                />
              )}
            </>

            <rect
              width="100%"
              height="100%"
              fill="transparent"
              onMouseMove={handleMove}
              onTouchMove={handleMove}
              onClick={onClick}
              aria-label="Narrative rectangle marker"
              role="img"
            />
          </>
        )}
      </svg>
    </Wrapper>
  );
};
